import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Loader2, FileSpreadsheet, Trash2, ChevronRight, ArrowLeft,
  CheckCircle2, AlertTriangle, XCircle, Download, PlayCircle, RefreshCcw,
  UserPlus, Link2, SkipForward, ShieldAlert, Info, FileText,
} from "lucide-react";
import { toast } from "sonner";

const TONE = {
  ready:        { chip: "bg-emerald-100 text-[#39FF14] border-emerald-200", label: "Ready" },
  needs_review: { chip: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",       label: "Review" },
  error:        { chip: "bg-red-500/15 text-red-300 border-red-500/25",              label: "Error" },
  imported:     { chip: "bg-[#1A1F1C] text-[#F8F9FA] border-[#3A4640]",        label: "Imported" },
  skipped:      { chip: "bg-[#121815] text-[#8A8F98] border-[#3A4640]",        label: "Skipped" },
  pending:      { chip: "bg-[#121815] text-[#F8F9FA] border-[#3A4640]",        label: "Pending" },
};

const CONF_CHIP = {
  high:   "bg-emerald-100 text-[#39FF14] border-emerald-200",
  medium: "bg-yellow-500/15  text-yellow-200 border-yellow-500/30",
  low:    "bg-[#121815]  text-[#8A8F98] border-[#3A4640]",
  none:   "bg-red-500/10     text-red-300  border-red-500/25",
};

// ============================================================================
// Landing — list batches + upload
// ============================================================================
export default function DataMigration() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const isNonAdmin = !!(user && user.role !== "admin");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/migration/batches");
      setBatches(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load batches");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (isNonAdmin) return;
    load();
  }, [isNonAdmin]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/migration/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded — ${data.code}`);
      if (data.duplicate_file_of) {
        toast.warning(`Same file was uploaded before as ${data.duplicate_file_of.code} — review before importing.`);
      }
      navigate(`/admin/data-migration/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  // Guard placed AFTER all hooks so hook order is stable across renders.
  if (isNonAdmin) return <Navigate to="/" replace />;

  return (
    <div className="p-6 md:p-8" data-testid="migration-landing">
      <PageHeader
        title="Historical Data Migration"
        subtitle="Admin only. Upload historical Excel/xlsx workbooks — the engine adapts to each file's layout."
        action={
          <label className="cursor-pointer">
            <input type="file" accept=".xlsx,.xls" className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid="migration-upload-input" />
            <span className="inline-flex items-center bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] px-4 py-2 rounded-md font-heading text-sm">
              {uploading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />}
              Upload Historical File
            </span>
          </label>
        }
      />

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3 mb-6 text-sm text-yellow-200 flex items-start gap-2">
        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
        <div>
          <b>Admin-only workflow.</b> Uploads never auto-import — you'll analyze, map columns, review customer matches, and confirm before anything is written to Customer 360 or Engagement filters.
        </div>
      </div>

      <div className="bg-[#121815] border border-[#3A4640] rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-[#3A4640] flex items-center justify-between">
          <div className="text-sm font-heading font-semibold text-[#F8F9FA] uppercase tracking-wider">Import Batches</div>
          {loading && <Loader2 size={14} className="text-[#8A8F98] animate-spin" />}
        </div>

        {!loading && batches.length === 0 && (
          <div className="px-5 py-16 text-center" data-testid="migration-empty">
            <FileSpreadsheet size={40} className="mx-auto text-[#8A8F98]" />
            <div className="mt-3 text-sm text-[#8A8F98]">No historical files uploaded yet.</div>
            <div className="text-xs text-[#8A8F98] mt-1">Upload any .xlsx / .xls — the engine will inspect every sheet and column.</div>
          </div>
        )}

        {batches.map((b) => (
          <Link key={b.id} to={`/admin/data-migration/${b.id}`}
            className="block px-5 py-4 border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C] transition-colors"
            data-testid={`migration-batch-row-${b.code}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded bg-[#39FF14]/5 border border-emerald-100 flex items-center justify-center shrink-0">
                <FileSpreadsheet size={18} className="text-emerald-700" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-semibold text-[#F8F9FA] truncate">{b.file_name}</span>
                  <span className="text-[10px] font-mono text-[#8A8F98] bg-[#121815] px-1.5 py-0.5 rounded">{b.code}</span>
                  <span className={`text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border ${
                    b.status === "imported" ? "bg-emerald-100 text-[#39FF14] border-emerald-200"
                    : b.status === "partial" ? "bg-yellow-500/15 text-yellow-200 border-yellow-500/30"
                    : "bg-[#121815] text-[#F8F9FA] border-[#3A4640]"
                  }`}>{b.status}</span>
                </div>
                <div className="text-xs text-[#8A8F98] mt-1 flex flex-wrap gap-x-3">
                  <span>{formatDate(b.uploaded_at)}</span>
                  <span>by {b.uploaded_by_name}</span>
                  <span>{(b.sheets || []).length} sheet{(b.sheets || []).length !== 1 && "s"}</span>
                  <span>{b.totals?.rows_total || 0} rows</span>
                  {b.totals?.rows_imported > 0 && <span className="text-emerald-700">{b.totals.rows_imported} imported</span>}
                  {b.totals?.rows_review > 0 && <span className="text-yellow-300">{b.totals.rows_review} review</span>}
                  {b.totals?.rows_errors > 0 && <span className="text-red-300">{b.totals.rows_errors} errors</span>}
                </div>
              </div>
              <ChevronRight size={16} className="text-[#8A8F98] shrink-0" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}


// ============================================================================
// Batch Detail — mapping, staging, review, import
// ============================================================================
export function DataMigrationBatch() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [canonical, setCanonical] = useState([]);
  const [rowsFilter, setRowsFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [staging, setStaging] = useState(false);
  const [importing, setImporting] = useState(false);
  const isNonAdmin = !!(user && user.role !== "admin");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: c }] = await Promise.all([
        api.get(`/migration/batches/${id}`),
        api.get("/migration/canonical-fields"),
      ]);
      setBatch(b);
      setCanonical(c.fields || []);
      if (b?.status !== "analyzed") {
        const rr = await api.get(`/migration/batches/${id}/rows`, { params: { decision: rowsFilter, limit: 200 } });
        setRows(rr.data.items || []);
        setRowsTotal(rr.data.total || 0);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load batch");
      navigate("/admin/data-migration");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (isNonAdmin) return;
    load();
  }, [id, rowsFilter, isNonAdmin]);

  // Guard placed AFTER all hooks so hook order is stable across renders.
  if (isNonAdmin) return <Navigate to="/" replace />;

  const saveMapping = async (sheetName, mapping, categoryOverride, headerRowOverride) => {
    try {
      await api.put(`/migration/batches/${id}/sheets/${encodeURIComponent(sheetName)}/mapping`, {
        mapping, category_override: categoryOverride, header_row_override: headerRowOverride,
      });
      toast.success(`Mapping saved for ${sheetName}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save mapping"); }
  };

  const runStage = async () => {
    setStaging(true);
    try {
      const { data } = await api.post(`/migration/batches/${id}/stage`);
      toast.success(`Staged ${data.totals?.rows_staged || 0} rows`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Stage failed"); }
    finally { setStaging(false); }
  };

  const importReady = async () => {
    if (!window.confirm("Import all rows currently marked as Ready?")) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/migration/batches/${id}/import-ready`);
      toast.success(`Imported ${data.imported} rows — ${data.new_customers} new customers, ${data.linked_customers} linked`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
    finally { setImporting(false); }
  };

  const resolveRow = async (rowId, action, matchedCustomerId = null) => {
    try {
      await api.post(`/migration/rows/${rowId}/resolve`, { action, matched_customer_id: matchedCustomerId });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Resolve failed"); }
  };

  const downloadErrors = async () => {
    try {
      const resp = await api.get(`/migration/batches/${id}/error-report.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${batch.code}_review_and_errors.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const deleteBatch = async () => {
    if (!window.confirm(`Delete batch ${batch.code}? This is only allowed while nothing has been imported.`)) return;
    try {
      await api.delete(`/migration/batches/${id}`);
      toast.success("Batch deleted");
      navigate("/admin/data-migration");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  if (loading || !batch) return <SkeletonPage title kpi={false} table rows={6} cols={5} />;

  const t = batch.totals || {};

  return (
    <div className="p-6 md:p-8" data-testid="migration-batch-detail">
      {/* Top bar */}
      <div className="mb-4 flex items-center gap-3">
        <Link to="/admin/data-migration" className="text-sm text-[#8A8F98] hover:text-[#F8F9FA] flex items-center gap-1" data-testid="back-to-batches">
          <ArrowLeft size={14} /> All batches
        </Link>
      </div>

      <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <FileSpreadsheet size={18} className="text-emerald-700 shrink-0" />
              <h2 className="text-xl font-heading font-bold text-[#F8F9FA] truncate">{batch.file_name}</h2>
              <span className="text-[11px] font-mono text-[#8A8F98] bg-[#121815] px-1.5 py-0.5 rounded">{batch.code}</span>
              <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-[#121815] text-[#F8F9FA] border-[#3A4640]">{batch.status}</span>
            </div>
            <div className="text-xs text-[#8A8F98] mt-1">
              Uploaded {formatDate(batch.uploaded_at)} by {batch.uploaded_by_name} · {batch.sheets?.length || 0} sheets · {t.rows_total || 0} data rows
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={runStage} disabled={staging} data-testid="stage-btn">
              {staging ? <Loader2 size={13} className="animate-spin mr-2" /> : <RefreshCcw size={13} className="mr-2" />}
              {batch.status === "analyzed" ? "Analyse & match" : "Re-stage"}
            </Button>
            <Button variant="outline" onClick={downloadErrors} data-testid="download-error-report-btn">
              <Download size={13} className="mr-2" /> Error / review report
            </Button>
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-[#0B0F0C]" onClick={importReady} disabled={importing || !(t.rows_ready > 0)} data-testid="import-ready-btn">
              {importing ? <Loader2 size={13} className="animate-spin mr-2" /> : <PlayCircle size={13} className="mr-2" />}
              Import {t.rows_ready || 0} ready
            </Button>
            {(t.rows_imported || 0) === 0 && (
              <button onClick={deleteBatch} className="p-2 text-[#8A8F98] hover:text-red-600" title="Delete batch" data-testid="delete-batch-btn">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
          <Stat label="Rows detected" value={t.rows_total} />
          <Stat label="Ready" value={t.rows_ready} tone="ready" />
          <Stat label="Review" value={t.rows_review} tone="needs_review" />
          <Stat label="Errors" value={t.rows_errors} tone="error" />
          <Stat label="Imported" value={t.rows_imported} tone="imported" />
        </div>
      </div>

      {/* Sheets & mapping */}
      <div className="space-y-4 mb-6" data-testid="sheets-list">
        {batch.sheets?.map((s) => (
          <SheetCard key={s.sheet_name} sheet={s} overrides={batch.sheet_overrides?.[s.sheet_name] || {}}
                     canonical={canonical} onSave={saveMapping} />
        ))}
      </div>

      {/* Rows / review */}
      {batch.status !== "analyzed" && (
        <div className="bg-[#121815] border border-[#3A4640] rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-[#3A4640] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-heading font-semibold text-[#F8F9FA] uppercase tracking-wider">Staged rows ({rowsTotal})</div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Filter</Label>
              <Select value={rowsFilter || "all"} onValueChange={(v) => setRowsFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm w-48" data-testid="rows-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="needs_review">Needs review</SelectItem>
                  <SelectItem value="error">Errors</SelectItem>
                  <SelectItem value="imported">Imported</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {rows.length === 0 && <div className="p-8 text-sm text-[#8A8F98] text-center">No rows match this filter.</div>}
            {rows.map((r) => <RowCard key={r.id} row={r} onResolve={resolveRow} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const chip = tone ? TONE[tone]?.chip : "bg-[#121815] border-[#3A4640]";
  return (
    <div className={`rounded-md border p-3 ${chip}`}>
      <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98]">{label}</div>
      <div className="text-2xl font-heading font-bold text-[#F8F9FA]">{value || 0}</div>
    </div>
  );
}

function SheetCard({ sheet, overrides, canonical, onSave }) {
  const [mapping, setMapping] = useState(overrides.mapping || {});
  const [categoryOverride, setCategoryOverride] = useState(overrides.category_override || "");
  const [headerRow, setHeaderRow] = useState(overrides.header_row_override || sheet.detected_header_row);
  const [expanded, setExpanded] = useState(false);
  const dirty = useMemo(() => {
    return JSON.stringify(mapping) !== JSON.stringify(overrides.mapping || {}) ||
           (overrides.category_override || "") !== (categoryOverride || "") ||
           (overrides.header_row_override || sheet.detected_header_row) !== headerRow;
  }, [mapping, categoryOverride, headerRow, overrides, sheet.detected_header_row]);

  return (
    <div className="bg-[#121815] border border-[#3A4640] rounded-lg overflow-hidden" data-testid={`sheet-card-${sheet.sheet_name}`}>
      <button className="w-full flex items-center justify-between px-5 py-3 border-b border-[#3A4640] bg-[#0B0F0C] hover:bg-[#121815] transition-colors"
              onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="font-heading font-semibold text-[#F8F9FA]">{sheet.sheet_name}</div>
          <div className="text-xs text-[#8A8F98]">
            Row {sheet.detected_header_row} header · {sheet.row_count_non_empty} rows · {sheet.columns?.length || 0} cols
          </div>
          {sheet.category_hint && (
            <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-emerald-100 text-[#39FF14] border-emerald-200">
              {sheet.category_hint} <span className="text-emerald-600 normal-case ml-1">(from {sheet.category_source})</span>
            </span>
          )}
          {sheet.warnings?.length > 0 && (
            <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-yellow-500/15 text-yellow-200 border-yellow-500/30 flex items-center gap-1">
              <AlertTriangle size={9} /> {sheet.warnings.length}
            </span>
          )}
        </div>
        <ChevronRight size={16} className={`text-[#8A8F98] transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && (
        <div className="p-5 space-y-4">
          {sheet.warnings?.length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-2 text-xs text-yellow-200">
              <div className="flex items-center gap-1 font-heading uppercase tracking-wider text-[10px] mb-1"><Info size={11} /> Warnings</div>
              <ul className="list-disc pl-5">{sheet.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Category context override</Label>
              <Input value={categoryOverride} onChange={(e) => setCategoryOverride(e.target.value)}
                placeholder={sheet.category_hint || "e.g. Power Tiller"} data-testid={`sheet-category-input-${sheet.sheet_name}`} />
              <div className="text-[11px] text-[#8A8F98] mt-1">Applied when a row has no equipment-category column.</div>
            </div>
            <div>
              <Label>Header row (1-indexed)</Label>
              <Input type="number" min={1} value={headerRow} onChange={(e) => setHeaderRow(parseInt(e.target.value || "1", 10))} data-testid={`sheet-header-row-input-${sheet.sheet_name}`} />
            </div>
          </div>

          <div className="border border-[#3A4640] rounded-md overflow-hidden">
            <div className="grid grid-cols-[70px,1fr,220px,90px,1fr] px-3 py-2 border-b border-[#3A4640] bg-[#0B0F0C] text-[10px] uppercase font-heading tracking-wider text-[#8A8F98]">
              <div>Col</div><div>Source header</div><div>Target field</div><div>Confidence</div><div>Sample</div>
            </div>
            {sheet.columns?.map((c) => {
              const target = mapping[c.source_header] !== undefined ? mapping[c.source_header] : c.target_field;
              return (
                <div key={c.index} className="grid grid-cols-[70px,1fr,220px,90px,1fr] px-3 py-2 border-b border-[#3A4640] last:border-0 items-center text-sm"
                     data-testid={`col-row-${sheet.sheet_name}-${c.letter}`}>
                  <div className="font-mono text-[#8A8F98] text-xs">{c.letter}</div>
                  <div className="font-medium text-[#F8F9FA] truncate">{c.source_header || <span className="italic text-[#8A8F98]">(blank)</span>}</div>
                  <div>
                    <Select value={target} onValueChange={(v) => setMapping({ ...mapping, [c.source_header]: v })}>
                      <SelectTrigger className="h-8 text-sm" data-testid={`map-target-${sheet.sheet_name}-${c.letter}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-80">
                        {canonical.map((f) => (
                          <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <span className={`text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border ${CONF_CHIP[c.confidence] || CONF_CHIP.none}`}>
                      {c.confidence}
                    </span>
                  </div>
                  <div className="text-xs text-[#8A8F98] truncate">{c.sample?.slice(0, 3).join(" · ")}</div>
                </div>
              );
            })}
          </div>

          {sheet.sections?.length > 1 && (
            <div className="border border-[#3A4640] rounded-md overflow-hidden" data-testid={`sections-panel-${sheet.sheet_name}`}>
              <div className="px-3 py-2 border-b border-[#3A4640] bg-[#0B0F0C] text-[10px] uppercase font-heading tracking-wider text-[#8A8F98]">
                {sheet.sections.length} sections detected in this sheet
              </div>
              {sheet.sections.map((s) => (
                <div key={s.section_index}
                     className="px-3 py-2 border-b border-[#3A4640] last:border-0 text-xs text-[#F8F9FA]"
                     data-testid={`section-row-${sheet.sheet_name}-${s.section_index}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] text-[#8A8F98]">§{s.section_index + 1}</span>
                    <span className="font-medium">{s.banner_text || "(no banner)"}</span>
                    {s.year_hint && <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-[#39FF14]/5 border-emerald-200 text-[#39FF14]">FY {s.year_hint}</span>}
                    {s.brand_hint && <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-blue-500/10 border-blue-500/25 text-blue-300">{s.brand_hint}</span>}
                    {s.category_hint && <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-yellow-500/10 border-yellow-500/30 text-yellow-300">{s.category_hint}</span>}
                    <span className="text-[#8A8F98]">rows {s.data_start}–{s.data_end} · {s.row_count_non_empty} non-empty</span>
                    {s.column_shift > 0 && <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-orange-50 border-orange-200 text-orange-800">shift +{s.column_shift}</span>}
                    {s.swap_pairs?.length > 0 && <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-orange-50 border-orange-200 text-orange-800">swap</span>}
                  </div>
                  {s.warnings?.length > 0 && (
                    <div className="mt-1 text-[11px] text-[#8A8F98]">
                      {s.warnings.join(" · ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant={dirty ? "default" : "outline"} onClick={() => onSave(sheet.sheet_name, mapping, categoryOverride || null, headerRow || null)}
              className={dirty ? "bg-[#39FF14] hover:bg-[#2ECC71]" : ""}
              disabled={!dirty}
              data-testid={`save-mapping-${sheet.sheet_name}`}>
              Save mapping
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RowCard({ row, onResolve }) {
  const [openCandidates, setOpenCandidates] = useState(false);
  const meta = TONE[row.decision] || TONE.pending;
  const hist = row.hist || {};
  const val = (k) => (hist[k] || {}).value || "";
  const cm = row.customer_match || {};

  return (
    <div className="px-5 py-3 border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C]/50" data-testid={`row-${row.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-semibold text-[#F8F9FA]">{val("customer_name") || <em>(no name)</em>}</span>
            <span className="text-[10px] font-mono text-[#8A8F98]">{row.sheet_name} · row {row.source_row}</span>
            <span className={`text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border ${meta.chip}`}>{meta.label}</span>
            {row.error && <span className="text-[11px] text-red-300"><XCircle size={11} className="inline mr-0.5" /> {row.error}</span>}
          </div>
          <div className="text-xs text-[#8A8F98] mt-1 flex flex-wrap gap-x-3">
            {val("mobile") && <span>📞 {val("mobile")}</span>}
            {val("village") && <span>🏘 {val("village")}</span>}
            {val("equipment_category") && <span className="font-heading uppercase text-[#39FF14]">{val("equipment_category")}</span>}
            {val("brand") && <span>{val("brand")} {val("model")}</span>}
            {val("historical_date") && <span>📅 {val("historical_date")}</span>}
          </div>
          {cm.status && cm.status !== "none" && (
            <div className="text-xs text-[#8A8F98] mt-1">
              Customer match: <b className={cm.status === "high" ? "text-emerald-700" : "text-yellow-300"}>{cm.status}</b>
              {cm.candidates?.length > 0 && (
                <button className="ml-2 underline hover:text-[#F8F9FA]" onClick={() => setOpenCandidates(!openCandidates)}>
                  {cm.candidates.length} candidate{cm.candidates.length !== 1 && "s"}
                </button>
              )}
              {cm.reasons?.length > 0 && <span className="ml-2 text-[#8A8F98]">· {cm.reasons.join(", ")}</span>}
            </div>
          )}
          {openCandidates && cm.candidates?.length > 0 && (
            <div className="mt-2 space-y-1.5 bg-[#0B0F0C] rounded p-2 border border-[#3A4640]">
              {cm.candidates.map((c) => (
                <div key={c.customer_id} className="flex items-center justify-between gap-2 text-xs">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#F8F9FA] truncate">{c.snapshot?.name} <span className="font-mono text-[#8A8F98]">{c.snapshot?.tzat_no}</span></div>
                    <div className="text-[#8A8F98]">{c.snapshot?.phone} · {c.snapshot?.village}, {c.snapshot?.district} · score {c.score} ({c.tier})</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onResolve(row.id, "confirm", c.customer_id)} data-testid={`confirm-match-${row.id}-${c.customer_id}`}>
                    <Link2 size={11} className="mr-1" /> Link
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        {row.decision === "needs_review" && (
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="outline" onClick={() => onResolve(row.id, "create_new")} data-testid={`create-new-${row.id}`}>
              <UserPlus size={11} className="mr-1" /> Create new
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onResolve(row.id, "skip")} className="text-[#8A8F98]" data-testid={`skip-row-${row.id}`}>
              <SkipForward size={11} className="mr-1" /> Skip
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
